'use client';

import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicroSource } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { PopupHeader, padded } from '../chrome';
import { CheckBigIcon, CrossBigIcon } from '../icons';
import { dispatchMicroProgressUpdated } from '../../_events';

/**
 * Quiz player — legacy-parity flow: ONE question at a time with
 * immediate per-question feedback, then a pass/fail result screen with
 * retry. Body shapes match the admin's {@code QuizBody} editor:
 *
 * <ul>
 *   <li>Single-question — {@code {question, options[], correctIndex,
 *       correctFeedback?, incorrectFeedback?}} at the body root.</li>
 *   <li>Multi-question — {@code {questions:[...], passingScore?,
 *       shuffleQuestions?, shuffleOptions?, isTrueFalse?}}.</li>
 * </ul>
 *
 * <p>Shuffling is a seeded Fisher-Yates: the permutation is computed
 * once per mount from a random seed held in state, so it never
 * re-shuffles mid-attempt (retry draws a new seed). {@code correctIndex}
 * stays in AUTHORING order — {@code optionOrder} translates the display
 * index back before scoring, so shuffled options score correctly.
 *
 * <p>Completion: a passing score fires {@code onComplete} with the
 * score payload — the popup forwards it into the {@code completed}
 * progress write. A failing attempt never completes, so its counts +
 * {@code result.quiz} echo are written directly here (no status).
 */

interface QuizQuestionBody {
  question?: string;
  questionDescription?: string | null;
  options?: string[];
  correctIndex?: number;
  correctFeedback?: string | null;
  incorrectFeedback?: string | null;
}

interface ShuffledQuestion {
  question: string;
  questionDescription: string | null;
  /** Options in DISPLAY order (post-shuffle). */
  options: string[];
  /** Correct option index in AUTHORING order. */
  correctIndex: number;
  correctFeedback: string | null;
  incorrectFeedback: string | null;
  /** Question index in AUTHORING order. */
  originalIndex: number;
  /** displayIndex → authoringIndex permutation for {@code options}. */
  optionOrder: number[];
}

/** Deterministic PRNG so a shuffle is stable for a given seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffledIndices(length: number, rand: () => number): number[] {
  const order = Array.from({ length }, (_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const a = order[i]!;
    order[i] = order[j]!;
    order[j] = a;
  }
  return order;
}

export function normaliseQuizBody(
  rawBody: unknown,
  seed: number,
): { questions: ShuffledQuestion[]; passingScore: number; isTrueFalse: boolean } {
  const body = (rawBody ?? {}) as {
    questions?: QuizQuestionBody[];
    passingScore?: number;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    isTrueFalse?: boolean;
  } & QuizQuestionBody;

  const multi = Array.isArray(body.questions) && body.questions.length > 0;
  // Single-question: getting it right = pass.
  const passingScore = multi
    ? typeof body.passingScore === 'number'
      ? body.passingScore
      : 60
    : 100;

  const rawQuestions: QuizQuestionBody[] = multi
    ? (body.questions as QuizQuestionBody[])
    : [
        {
          question: body.question ?? '',
          questionDescription: body.questionDescription,
          options: body.options ?? [],
          correctIndex: body.correctIndex ?? -1,
          correctFeedback: body.correctFeedback,
          incorrectFeedback: body.incorrectFeedback,
        },
      ];
  const valid = rawQuestions.filter((q) => (q.options?.length ?? 0) > 0);

  const rand = mulberry32(seed);
  const questionOrder =
    body.shuffleQuestions === true && valid.length > 1
      ? shuffledIndices(valid.length, rand)
      : valid.map((_, i) => i);

  const questions: ShuffledQuestion[] = questionOrder.map((originalIndex) => {
    const q = valid[originalIndex]!;
    const opts = q.options ?? [];
    const optionOrder =
      body.shuffleOptions === true && opts.length > 1
        ? shuffledIndices(opts.length, rand)
        : opts.map((_, i) => i);
    return {
      question: q.question ?? '',
      questionDescription: q.questionDescription ?? null,
      options: optionOrder.map((i) => opts[i]!),
      correctIndex: q.correctIndex ?? -1,
      correctFeedback: q.correctFeedback ?? null,
      incorrectFeedback: q.incorrectFeedback ?? null,
      originalIndex,
      optionOrder,
    };
  });

  // T/F: explicit flag wins; otherwise inferred for a single 2-option
  // question (multi-question quizzes get per-question inference below).
  const isTrueFalse =
    typeof body.isTrueFalse === 'boolean'
      ? body.isTrueFalse
      : questions.length === 1 && (questions[0]?.options.length ?? 0) === 2;

  return { questions, passingScore, isTrueFalse };
}

const ACCENT = 'var(--color-warning, #f97316)';
const SUCCESS = 'var(--color-success-500, #16a34a)';
const DANGER = 'var(--color-danger, #dc2626)';
const MUTED = 'var(--color-text-secondary, #64748b)';

const primaryButtonStyle: CSSProperties = {
  padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
  border: 'none',
  borderRadius: 8,
  background: 'var(--color-text-primary, #18181b)',
  color: 'var(--color-text-on-brand, #fff)',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm, 14px)',
  cursor: 'pointer',
};

export function QuizRenderer({ item, onComplete, onClose }: RendererProps) {
  const micro = useMicroSource();
  const { t } = useTranslation('site-builder-blocks');
  // Random seed per attempt, held in state so the shuffle is stable
  // across re-renders (and hydration-safe — the popup mounts client-only
  // and the seed is created once in the lazy initializer). Retry draws a
  // new seed, which re-shuffles.
  const [seed, setSeed] = useState<number>(() => Math.floor(Math.random() * 0x7fffffff));
  const {
    questions,
    passingScore,
    isTrueFalse: bodyIsTrueFalse,
  } = useMemo(() => normaliseQuizBody(item.body, seed), [item.body, seed]);

  const [questionIdx, setQuestionIdx] = useState(0);
  /** DISPLAY index picked per question (null = unanswered). */
  const [selected, setSelected] = useState<(number | null)[]>(() => questions.map(() => null));
  const [submitted, setSubmitted] = useState<boolean[]>(() => questions.map(() => false));
  const [finished, setFinished] = useState(false);
  const completeFiredRef = useRef(false);

  if (questions.length === 0) {
    return (
      <div style={padded}>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
        <div style={{ marginTop: 16, color: MUTED }}>{t('microQuiz.empty')}</div>
      </div>
    );
  }

  const current = questions[questionIdx]!;
  const currentSelected = selected[questionIdx] ?? null;
  const currentSubmitted = submitted[questionIdx] === true;
  // Display → authoring translation; identity when shuffleOptions is off.
  const currentSelectedAuthoring =
    currentSelected !== null ? (current.optionOrder[currentSelected] ?? null) : null;
  const isCorrect =
    currentSubmitted &&
    currentSelectedAuthoring !== null &&
    currentSelectedAuthoring === current.correctIndex;
  const isTrueFalse = bodyIsTrueFalse || current.options.length === 2;

  const correctCount = questions.reduce((acc, q, i) => {
    const sel = selected[i];
    const selAuthoring = sel != null ? q.optionOrder[sel] : null;
    return acc + (selAuthoring === q.correctIndex ? 1 : 0);
  }, 0);
  const scorePercent = Math.round((correctCount / questions.length) * 100);
  const passed = scorePercent >= passingScore;

  const pick = (displayIdx: number) => {
    if (currentSubmitted) return;
    setSelected((prev) => {
      const next = [...prev];
      next[questionIdx] = displayIdx;
      return next;
    });
  };

  const submitCurrent = () => {
    if (currentSelected === null) return;
    setSubmitted((prev) => {
      const next = [...prev];
      next[questionIdx] = true;
      return next;
    });
  };

  const finish = () => {
    if (!completeFiredRef.current) {
      completeFiredRef.current = true;
      if (passed) {
        // The popup forwards this payload into its
        // {@code progress.update({status: 'completed', ...})} write.
        onComplete({
          correctCount,
          totalCount: questions.length,
          result: {
            quiz: { correctCount, totalCount: questions.length, scorePercent, passed },
          },
        });
      } else if (micro?.progress) {
        // Failed attempt never completes — record its counts + echo
        // directly (no status), same pattern as PollRenderer's direct
        // polls.vote write.
        void micro.progress
          .update({
            itemId: item.id,
            correctCount,
            totalCount: questions.length,
            result: {
              quiz: {
                scorePercent,
                passed,
                answers: questions.map((q, i) => {
                  const sel = selected[i];
                  const selAuthoring = sel != null ? (q.optionOrder[sel] ?? -1) : -1;
                  return {
                    questionIndex: q.originalIndex,
                    selectedIndex: selAuthoring,
                    correct: selAuthoring === q.correctIndex,
                  };
                }),
              },
            },
          })
          .then(() => dispatchMicroProgressUpdated())
          .catch(() => undefined);
      }
    }
    setFinished(true);
  };

  const advance = () => {
    if (questionIdx + 1 < questions.length) setQuestionIdx(questionIdx + 1);
    else finish();
  };

  const retry = () => {
    completeFiredRef.current = false;
    setFinished(false);
    setQuestionIdx(0);
    const nextSeed = Math.floor(Math.random() * 0x7fffffff);
    setSeed(nextSeed);
    // Length is shuffle-independent, so resetting against the current
    // question count is safe even though the new order arrives next render.
    setSelected(questions.map(() => null));
    setSubmitted(questions.map(() => false));
  };

  if (finished) {
    return (
      <QuizSummary
        item={item}
        questions={questions}
        selected={selected}
        passingScore={passingScore}
        onRetry={retry}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, paddingTop: 0 }}>
        {current.question && (
          <div style={{ fontSize: 'var(--font-size-base, 15px)', fontWeight: 600 }}>
            {current.question}
          </div>
        )}
        {current.questionDescription && (
          <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-sm, 14px)', color: MUTED }}>
            {current.questionDescription}
          </p>
        )}

        {isTrueFalse ? (
          // True/False — two big chips side by side ("veya" pair styling).
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              alignItems: 'stretch',
              marginTop: 20,
            }}
          >
            {current.options.map((opt, i) => {
              const isSel = currentSelected === i;
              const authoringIdx = current.optionOrder[i];
              const isThisCorrect = currentSubmitted && authoringIdx === current.correctIndex;
              const isThisWrong =
                currentSubmitted && isSel && authoringIdx !== current.correctIndex;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(i)}
                  disabled={currentSubmitted}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                    padding: '24px 16px',
                    borderRadius: 16,
                    font: 'inherit',
                    border: `2px solid ${
                      isThisCorrect
                        ? SUCCESS
                        : isThisWrong
                          ? DANGER
                          : isSel
                            ? ACCENT
                            : 'var(--color-border, rgba(0,0,0,0.08))'
                    }`,
                    background: isThisCorrect
                      ? 'rgba(22,163,74,0.08)'
                      : isThisWrong
                        ? 'rgba(220,38,38,0.08)'
                        : isSel
                          ? 'rgba(249,115,22,0.06)'
                          : 'var(--color-surface-card, #fff)',
                    cursor: currentSubmitted ? 'default' : 'pointer',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'inline-grid',
                      placeItems: 'center',
                      background: i === 0 ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.10)',
                      color: i === 0 ? SUCCESS : DANGER,
                    }}
                  >
                    {i === 0 ? <CheckBigIcon /> : <CrossBigIcon />}
                  </span>
                  <span style={{ fontSize: 'var(--font-size-md, 16px)', fontWeight: 600 }}>
                    {opt}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          // Standard list — A/B/C/D option discs.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            {current.options.map((opt, i) => {
              const isSel = currentSelected === i;
              const authoringIdx = current.optionOrder[i];
              const isThisCorrect = currentSubmitted && authoringIdx === current.correctIndex;
              const isThisWrong =
                currentSubmitted && isSel && authoringIdx !== current.correctIndex;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(i)}
                  disabled={currentSubmitted}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: 'var(--space-3, 12px) var(--space-3-5, 14px)',
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: 'var(--font-size-sm, 14px)',
                    color: 'var(--color-text-primary, #0f172a)',
                    borderRadius: 12,
                    border: `2px solid ${
                      isThisCorrect
                        ? SUCCESS
                        : isThisWrong
                          ? DANGER
                          : isSel
                            ? ACCENT
                            : 'var(--color-border, rgba(0,0,0,0.08))'
                    }`,
                    background: isThisCorrect
                      ? 'rgba(22,163,74,0.06)'
                      : isThisWrong
                        ? 'rgba(220,38,38,0.06)'
                        : isSel
                          ? 'rgba(249,115,22,0.06)'
                          : 'var(--color-surface-card, #fff)',
                    cursor: currentSubmitted ? 'default' : 'pointer',
                  }}
                >
                  <OptionDisc letter={String.fromCharCode(65 + i)} selected={isSel} />
                  <span style={{ flex: 1 }}>{opt}</span>
                </button>
              );
            })}
          </div>
        )}

        {!currentSubmitted && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginTop: 20,
              paddingTop: 16,
              borderTop: '1px solid var(--color-border, rgba(0,0,0,0.06))',
            }}
          >
            <span style={{ fontSize: '13px', color: MUTED, whiteSpace: 'nowrap' }}>
              {t('microQuiz.questionLabel')}{' '}
              <strong style={{ color: ACCENT }}>{questionIdx + 1}</strong> / {questions.length}
            </span>
            <QuizProgressBar value={questionIdx + 1} max={questions.length} />
            <button
              type="button"
              onClick={submitCurrent}
              disabled={currentSelected === null}
              style={{
                ...primaryButtonStyle,
                opacity: currentSelected === null ? 0.5 : 1,
                cursor: currentSelected === null ? 'not-allowed' : 'pointer',
              }}
            >
              {t('microQuiz.submit')}
            </button>
          </div>
        )}

        {currentSubmitted && (
          <div
            style={{
              marginTop: 18,
              marginBottom: 8,
              padding: '14px 18px',
              background: isCorrect ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
              border: `1px solid ${isCorrect ? 'rgba(22,163,74,0.3)' : 'rgba(220,38,38,0.3)'}`,
              borderRadius: 12,
            }}
          >
            <strong style={{ color: isCorrect ? SUCCESS : DANGER }}>
              {isCorrect ? t('microQuiz.correct') : t('microQuiz.wrong')}
            </strong>
            {(isCorrect ? current.correctFeedback : current.incorrectFeedback) && (
              <p
                style={{
                  margin: '6px 0 12px',
                  fontSize: 'var(--font-size-sm, 14px)',
                  color: 'var(--color-text-primary, #0f172a)',
                }}
              >
                {isCorrect ? current.correctFeedback : current.incorrectFeedback}
              </p>
            )}
            <button type="button" onClick={advance} style={{ ...primaryButtonStyle, marginTop: 8 }}>
              {questionIdx + 1 < questions.length
                ? t('microQuiz.nextQuestion', {
                    current: questionIdx + 2,
                    total: questions.length,
                  })
                : t('microQuiz.seeResults')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * End-of-quiz result screen: pass/fail badge, big score, per-question
 * recap (✓/✗ per question — no option replay), retry when failed.
 */
function QuizSummary({
  item,
  questions,
  selected,
  passingScore,
  onRetry,
  onClose,
}: {
  item: RendererProps['item'];
  questions: ShuffledQuestion[];
  selected: (number | null)[];
  passingScore: number;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const correctCount = questions.reduce((acc, q, i) => {
    const sel = selected[i];
    const selAuthoring = sel != null ? q.optionOrder[sel] : null;
    return acc + (selAuthoring === q.correctIndex ? 1 : 0);
  }, 0);
  const scorePercent = Math.round((correctCount / questions.length) * 100);
  const passed = scorePercent >= passingScore;

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} />
      <div style={{ ...padded, paddingTop: 0 }}>
        <div
          style={{
            padding: 24,
            background: passed ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
            border: `1px solid ${passed ? 'rgba(22,163,74,0.3)' : 'rgba(220,38,38,0.3)'}`,
            borderRadius: 16,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-wider, 0.04em)',
              color: passed ? SUCCESS : DANGER,
              marginBottom: 8,
            }}
          >
            {passed ? t('microQuiz.passed') : t('microQuiz.failed')}
          </div>
          <div
            style={{
              fontSize: '3rem',
              fontWeight: 800,
              lineHeight: 1,
              color: 'var(--color-text-primary, #0f172a)',
            }}
          >
            {scorePercent}%
          </div>
          <div style={{ marginTop: 8, fontSize: 'var(--font-size-sm, 14px)', color: MUTED }}>
            {t('microQuiz.scoreSummary', {
              correct: correctCount,
              total: questions.length,
              passing: passingScore,
            })}
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <h3
            style={{
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 700,
              margin: '0 0 10px',
            }}
          >
            {t('microQuiz.questionsHeading')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {questions.map((q, i) => {
              const sel = selected[i];
              const selAuthoring = sel != null ? q.optionOrder[sel] : null;
              const isThisCorrect = selAuthoring === q.correctIndex;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 12px',
                    background: 'var(--color-surface-page, #f8fafc)',
                    borderRadius: 10,
                    fontSize: 'var(--font-size-sm, 14px)',
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: isThisCorrect ? SUCCESS : DANGER,
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                      display: 'inline-grid',
                      placeItems: 'center',
                      marginTop: 1,
                    }}
                  >
                    {isThisCorrect ? '✓' : '✗'}
                  </span>
                  <span style={{ flex: 1 }}>{q.question}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          {!passed && (
            <button
              type="button"
              onClick={onRetry}
              style={{ ...primaryButtonStyle, flex: 1, padding: '12px' }}
            >
              {t('microQuiz.retry')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              ...primaryButtonStyle,
              flex: 1,
              padding: '12px',
              ...(passed
                ? {}
                : {
                    background: 'transparent',
                    color: 'var(--color-text-primary, #18181b)',
                    border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
                  }),
            }}
          >
            {t('microQuiz.done')}
          </button>
        </div>
      </div>
    </>
  );
}

function OptionDisc({ letter, selected }: { letter: string; selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        display: 'inline-grid',
        placeItems: 'center',
        flexShrink: 0,
        background: selected ? ACCENT : 'var(--color-surface-page, #f3f4f6)',
        color: selected ? '#fff' : MUTED,
        fontSize: '13px',
        fontWeight: 700,
      }}
    >
      {letter}
    </span>
  );
}

function QuizProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max === 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div
      style={{
        flex: 1,
        height: 6,
        borderRadius: 999,
        background: 'var(--color-surface-page, #f1f5f9)',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: ACCENT }} />
    </div>
  );
}
